import { useTranslation } from 'react-i18next'
import { softBadgeColors } from '@structural-climbing/ui'
import { type Discipline, gradeDisplay } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'
import { SurfaceCard } from '../../design/SurfaceCard'
import type { MyProgress } from './useMyProgress'

function GradeBadge({ discipline, rank }: { discipline: Discipline; rank: number }) {
  const gradeConfig = useGymGradeConfig()
  const grade = gradeDisplay(gradeConfig, rank, discipline)
  const badge = softBadgeColors(grade.color)
  return (
    <span
      className="rounded px-2 py-0.5 text-lg font-semibold"
      style={{ backgroundColor: badge.bg, color: badge.fg }}
    >
      {grade.label}
    </span>
  )
}

/**
 * The hardest grade topped this window, per discipline, next to the best ever.
 * Ranks only compare within one scale, so bouldering and lead never share a row.
 */
export function HardestTopCard({ data, periodLabel }: { data: MyProgress; periodLabel: string }) {
  const { t } = useTranslation()
  const disciplines = (['boulder', 'lead'] as const).filter(
    (d) => data.discipline === 'all' || data.discipline === d,
  )
  const rows = disciplines.filter((d) => data.allTime.hardest[d] !== null)
  if (rows.length === 0) return null

  return (
    <SurfaceCard className="p-5">
      <div data-testid="hardest-card">
        <p className="text-sm font-semibold">{t('progress.hardestTitle')}</p>
        <p className="mb-4 text-xs text-foreground/50">{t('progress.hardestSubtitle')}</p>
        <div className="flex flex-col gap-4">
          {rows.map((d) => {
            const now = data.current.hardest[d]
            const best = data.allTime.hardest[d]!
            const isRecord = now !== null && now >= best
            return (
              <div key={d} className="flex flex-col gap-2">
                <span className="text-xs font-medium text-foreground/60">
                  {d === 'lead' ? t('routes.disciplineLead') : t('routes.disciplineBouldering')}
                </span>
                {/* Both numbers carry their own caption. Unlabelled, "7" beside
                  "8" reads as a score out of something. */}
                <div className="flex items-end gap-6">
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-foreground/45">
                      {t('progress.hardestInPeriod', { period: periodLabel })}
                    </span>
                    {now !== null ? (
                      <GradeBadge discipline={d} rank={now} />
                    ) : (
                      <span className="text-sm text-foreground/40">
                        {t('progress.hardestNone')}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-foreground/45">
                      {t('progress.hardestAllTime')}
                    </span>
                    <div className="flex items-center gap-2">
                      <GradeBadge discipline={d} rank={best} />
                      {isRecord && (
                        <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-bold uppercase text-warning-700">
                          🏆 {t('progress.personalBest')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </SurfaceCard>
  )
}
